import {Typography, Grid, Box, Link} from '@mui/material';
import Axios from 'axios';
import {useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';

import type {AppState} from '@jobseeker/common-core/types';
import {
  cities,
  linkageCompanies,
  HIRIST_CITY_LINKAGE,
} from '@jobseeker/lotus/constants';
import {getWindow} from 'ssr-window';
import {usePathname} from 'next/navigation';
import {openUrl, deferTask} from '@jobseeker/common-core/utils/common';

const window = getWindow();

const isAppHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

const getLinkageCompanyList = () => {
  return {
    data: linkageCompanies,
  };
};

function replaceAll(string: any, stringToReplace: any, replaceWith: any) {
  return string.split(stringToReplace).join(replaceWith);
}

function sanitizeTag(tag: any) {
  tag = replaceAll(tag, '+', '-plus');
  tag = replaceAll(tag, 'c#', 'c-sharp');
  tag = replaceAll(tag, '#', 'hash');
  tag = replaceAll(tag, '.', '');
  return tag;
}

function parseFetchedInterlinkingData(categoryName: string) {
  const {tagName, tagNameLower} = {tagName: null, tagNameLower: null};
  const prefix = categoryName || tagName;
  const prefixLower = sanitizeTag(
    categoryName
      .replace(/[/\s]+/g, '-')
      .toLowerCase()
      .replace(/&-/g, '') ||
      tagNameLower ||
      'it'
  );
  const heading = `Trending ${prefix ? prefix + ' ' : ''}jobs in your city`;
  const pathname = usePathname(); // e.g., /abc/xyz-jobs-in-xyz
  const currentCity = pathname.split('/').pop();
  const sourceCities = isAppHirist
    ? HIRIST_CITY_LINKAGE.map(lower =>
        cities.find(c => c.locationNameLower === lower)
      ).filter(Boolean)
    : cities;
  const listOfLinks = sourceCities
    .filter(
      e =>
        !currentCity?.includes('jobs-in') ||
        currentCity?.split('jobs-in-')[1] != e?.locationNameLower
    )
    .map((city: any) => {
      const {locationName, locationNameLower} = city;
      return {
        link: `/${prefixLower}-jobs-in-${locationNameLower}`,
        text: (prefix || '') + ' Jobs in ' + locationName,
      };
    });

  const styleClass = 'category-page-list';
  const screenWidth = window.innerWidth;
  const citiesLength: number = sourceCities.length || 0;
  let colCount = prefixLower == 'it' ? 3 : Math.ceil(sourceCities.length / 3);

  if (screenWidth <= 500) {
    colCount = citiesLength + 1;
  }

  if (screenWidth > 500 && screenWidth <= 900) {
    colCount = citiesLength / 2 + 1;
  }

  if (screenWidth > 990 && screenWidth <= 1200) {
    colCount = citiesLength / 2 + 1;
  }

  const data = [
    {
      colCount,
      heading,
      styleClass,
      data: listOfLinks,
    },
  ];

  return data;
}

interface CityPageListingProps {
  type: string;
  catId?: string | number;
}

const CityPageListingWrapper: React.FC<CityPageListingProps> = ({type}) => {
  const categoryname = useSelector(
    (state: AppState) => state.app.context.navigation.slug,
    shallowEqual
  );
  const [list, setList] = useState<any[]>(
    type == 'company'
      ? getLinkageCompanyList().data[0]
      : parseFetchedInterlinkingData(categoryname)
  );

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#fffcf7',
          border: '1px solid #f5e7d1',
          padding: 2,
          borderRadius: '8px',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: '180px',
          ml: '74px',
          mt: 2,
          mr: {xxs: 1.5, md: 4},
        }}
      >
        {type == 'company' ? (
          <>
            <>
              <Typography
                component="h2"
                sx={{fontSize: 16, fontWeight: 600, mb: 2}}
              >
                Explore Jobs based in trending companies
              </Typography>
              <Grid container spacing={2} ml={'0px !important'}>
                {list?.map((l: any, index: number) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={index}
                    pt={'8px !important'}
                  >
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${l.link.replace(/\.html$/, '')}`}
                      onClick={e => {
                        e.preventDefault();
                        const linkUrl = l.link.replace(/\.html$/, '');
                        deferTask(() => openUrl(linkUrl, '_blank'));
                      }}
                      sx={{color: 'rgba(0,0,0,.8)', textDecoration: 'none'}}
                    >
                      <Typography
                        component="li"
                        sx={{fontSize: '13px', fontWeight: 400}}
                      >
                        {type == 'company' ? `Jobs in ${l.name}` : l.text}
                      </Typography>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </>
          </>
        ) : (
          <>
            {list.map((l: any) => (
              <Typography
                component="h2"
                sx={{fontSize: 16, fontWeight: 600, mb: 2}}
              >
                {type == 'company'
                  ? 'Explore Jobs based in trending companies'
                  : l.heading}
              </Typography>
            ))}
            <Grid container spacing={2} ml={'0px !important'}>
              {list[0]?.data?.map((l: any, index: number) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={6}
                  pl={3}
                  key={index}
                  pt={'8px !important'}
                >
                  <Link
                    href={l.link.replace(/\.html$/, '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => {
                      e.preventDefault();
                      const linkUrl = l.link.replace(/\.html$/, '');
                      deferTask(() => openUrl(linkUrl, '_blank'));
                    }}
                    sx={{color: 'rgba(0,0,0,.8)', textDecoration: 'none'}}
                  >
                    <Typography
                      component="li"
                      sx={{fontSize: '13px', fontWeight: 400}}
                    >
                      {type == 'company' ? `Jobs in${l.name}` : l.text}
                    </Typography>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </>
  );
};
export default CityPageListingWrapper;
